import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FiSearch, FiX, FiUser,
   FiFileText,
  FiChevronRight, FiChevronDown,
  FiArrowLeft, FiArrowRight,
} from 'react-icons/fi';
import './CommissionScan.css';

/* ── env / auth ── */
const getToolboxToken  = () => localStorage.getItem('toolboxToken') || '';
const getNcToken   = () => localStorage.getItem('amsToken') || '';

/* ── types ── */
export interface AmsFileItem {
  id: string;
  name: string;
  type: 'folder' | 'file';
  description?: string;
  size?: string;
  uploader?: string;
  created?: string;
  lastModified?: string;
  children?: AmsFileItem[];
  downloadUrl?: string;
  parentFolderId?: string;
}

export interface AmsFileSelection {
  file: AmsFileItem;
  insuredId: string;
  folderId: string;
}

interface NowCertsClient {
  name: string;
  databaseId: string;
  eMail?: string;
}

interface Props {
  onFileSelected: (selection: AmsFileSelection | null) => void;
}

/* ─────────────────────────────────────────────── */
const CommissionScanClientFiles: React.FC<Props> = ({ onFileSelected }) => {
  const navigate = useNavigate();

  /* search */
  const [clientQuery,     setClientQuery]     = useState('');
  const [clientList,      setClientList]      = useState<NowCertsClient[]>([]);
  const [clientLoading,   setClientLoading]   = useState(false);
  const [selectedClient,  setSelectedClient]  = useState<NowCertsClient | null>(null);

  /* file browser */
  const [rootFolder,       setRootFolder]       = useState<AmsFileItem | null>(null);
  const [currentFolder,    setCurrentFolder]    = useState<AmsFileItem | null>(null);
  const [tableData,        setTableData]        = useState<AmsFileItem[]>([]);
  const [tableLoading,     setTableLoading]     = useState(false);
  const [fetchError,       setFetchError]       = useState('');
  const [expandedFolders,  setExpandedFolders]  = useState<Record<string, boolean>>({});
  const [selectedFile,     setSelectedFile]     = useState<AmsFileItem | null>(null);
  const [folderHistory,    setFolderHistory]    = useState<AmsFileItem[]>([]);
  const [historyIndex,     setHistoryIndex]     = useState(-1);

  /* ── client search (debounced) ── */
  const searchClients = useCallback((q: string) => {
    if (!q.trim()) { setClientList([]); return; }
    setClientLoading(true);
    axios.get(`${(process.env.REACT_APP_NOWCERTSAPI || '')}/api/nowcert/customerlist`, {
      params: { searchText: q, nowCertsToken: getNcToken() },
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then(r => { setClientList(r.data || []); setClientLoading(false); })
      .catch(e => { setClientLoading(false); if (e.response?.status === 401) navigate('/'); });
  }, [navigate]);

  useEffect(() => {
    const t = setTimeout(() => searchClients(clientQuery), 400);
    return () => clearTimeout(t);
  }, [clientQuery, searchClients]);

  /* ── fetch AMS files ── */
  const fetchFiles = useCallback(async (insuredId: string, folderId?: string) => {
    const url = `${(process.env.REACT_APP_NOWCERTSAPI || '')}/api/nowcert/getInsuredFilesList?insuredId=${insuredId}`
      + `&nctoken=${getNcToken()}`
      + `&folderId=${folderId || ''}`;
    const res = await axios.get(url, { headers: { Authorization: `Bearer ${getToolboxToken()}` } });
    return res.data;
  }, []);

  const mapFiles = (rawFiles: any[], parentId: string): AmsFileItem[] =>
    (rawFiles || []).map((f: any) => ({
      id:            f.databaseId,
      name:          f.name,
      type:          f.fileOrFolder === 'Folder' ? 'folder' : 'file',
      description:   f.fileOrFolder,
      size:          f.size?.toString() || '-',
      uploader:      f.creatorName || '-',
      created:       f.createDate  || '-',
      lastModified:  f.changeDate  || '-',
      downloadUrl:   f.downloadUrl,
      children:      f.fileOrFolder === 'Folder' ? [] : undefined,
      parentFolderId: f.parentId || parentId,
    }));

  /* load root files when client is selected */
  useEffect(() => {
    if (!selectedClient) return;
    setFetchError('');
    setRootFolder(null); setCurrentFolder(null);
    setTableData([]); setExpandedFolders({});
    setFolderHistory([]); setHistoryIndex(-1);
    setSelectedFile(null); onFileSelected(null);

    setTableLoading(true);
    fetchFiles(selectedClient.databaseId)
      .then(data => {
        const fd = data?.data || data;
        const root: AmsFileItem = {
          id:       fd.currentFolderId || selectedClient.databaseId,
          name:     fd.name || selectedClient.name || 'Root',
          type:     'folder',
          children: mapFiles(fd.files || [], fd.currentFolderId || selectedClient.databaseId),
        };
        setRootFolder(root);
        setCurrentFolder(root);
        setTableData(root.children || []);
        setExpandedFolders({ [root.id]: true });
        setFolderHistory([root]);
        setHistoryIndex(0);
        setTableLoading(false);
      })
      .catch(() => {
        setFetchError('Unable to load AMS files for this client. Please try again.');
        setTableLoading(false);
      });
  }, [selectedClient, fetchFiles, onFileSelected]);

  /* ── folder navigation ── */
  const openFolder = useCallback((folder: AmsFileItem, childrenOverride?: AmsFileItem[]) => {
    const children = childrenOverride ?? folder.children ?? [];
    const node: AmsFileItem = { ...folder, children };
    setCurrentFolder(node);
    setTableData(children);
    setExpandedFolders(p => ({ ...p, [folder.id]: true }));
    setFolderHistory(prev => {
      const base = historyIndex >= 0 ? prev.slice(0, historyIndex + 1) : [];
      const exists = base.findIndex(f => f.id === folder.id);
      if (exists !== -1) { setHistoryIndex(exists); return base; }
      const next = [...base, node];
      setHistoryIndex(next.length - 1);
      return next;
    });
  }, [historyIndex]);

  const updateChildrenInTree = (root: AmsFileItem, targetId: string, newChildren: AmsFileItem[]): AmsFileItem => {
    if (root.id === targetId) return { ...root, children: newChildren };
    if (root.children) return { ...root, children: root.children.map(c => updateChildrenInTree(c, targetId, newChildren)) };
    return root;
  };

  const toggleFolder = useCallback((item: AmsFileItem) => {
    if (expandedFolders[item.id]) {
      setExpandedFolders(p => ({ ...p, [item.id]: false }));
      return;
    }
    if (item.children && item.children.length === 0 && selectedClient) {
      setTableLoading(true);
      fetchFiles(selectedClient.databaseId, item.id)
        .then(data => {
          const newChildren = mapFiles(data?.data?.files || [], item.id);
          setRootFolder(prev => prev ? updateChildrenInTree(prev, item.id, newChildren) : prev);
          openFolder(item, newChildren);
          setTableLoading(false);
        })
        .catch(() => setTableLoading(false));
    } else if (item.type === 'folder') {
      openFolder(item);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedFolders, selectedClient, fetchFiles, openFolder]);

  const canGoBack    = historyIndex > 0;
  const canGoForward = historyIndex >= 0 && historyIndex < folderHistory.length - 1;

  const handleBack = () => {
    const newIdx = historyIndex - 1;
    const f = folderHistory[newIdx];
    if (!f) return;
    setHistoryIndex(newIdx); setCurrentFolder(f);
    setTableData(f.children || []);
    setExpandedFolders(p => ({ ...p, [f.id]: true }));
  };

  const handleForward = () => {
    const newIdx = historyIndex + 1;
    const f = folderHistory[newIdx];
    if (!f) return;
    setHistoryIndex(newIdx); setCurrentFolder(f);
    setTableData(f.children || []);
    setExpandedFolders(p => ({ ...p, [f.id]: true }));
  };

  /* breadcrumb path */
  const pathSegments = useMemo(() => {
    if (!rootFolder) return [] as AmsFileItem[];
    const target = currentFolder?.id || rootFolder.id;
    const path: AmsFileItem[] = [];
    const dfs = (node: AmsFileItem, acc: AmsFileItem[]): boolean => {
      const next = [...acc, node];
      if (node.id === target) { path.push(...next); return true; }
      for (const c of node.children || []) if (dfs(c, next)) return true;
      return false;
    };
    dfs(rootFolder, []);
    return path.length ? path : [rootFolder];
  }, [rootFolder, currentFolder]);

  /* ── helpers ── */
  const getIcon = (item: AmsFileItem) => {
    if (item.type === 'folder') return {  color: '#ec4899' };
    const ext = item.name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf')                return {      color: '#dc2626' };
    if (ext === 'xls' || ext === 'xlsx') return {  color: '#16a34a' };
    if (ext === 'doc' || ext === 'docx') return { color: '#1d4ed8' };
    return { Icon: FiFileText, color: '#6b7280' };
  };

  const fmtSize = (size?: string) => {
    if (!size || size === '-') return '—';
    const n = parseInt(size, 10);
    if (isNaN(n)) return size;
    return n >= 1048576 ? (n / 1048576).toFixed(2) + ' MB' : (n / 1024).toFixed(2) + ' KB';
  };

  /* ── folder tree (left panel) ── */
  const renderTreeItem = (item: AmsFileItem, level = 0): React.ReactNode => {
    const expanded = expandedFolders[item.id];
    return (
      <div key={item.id}>
        <div
          className={`cs-ams-tree-row${level === 0 ? ' cs-ams-tree-row--root' : ''}`}
          style={{ paddingLeft: level * 16 + 10 }}
          onClick={() => toggleFolder(item)}
        >
          {expanded
            ? <FiChevronDown  size={11} className="cs-ams-chevron" />
            : <FiChevronRight size={11} className="cs-ams-chevron" />}
          {/* <FiFolder size={13} className="cs-ams-tree-folder" /> */}
          <span className="cs-ams-tree-name" title={item.name}>{item.name}</span>
        </div>
        {expanded && (item.children || [])
          .filter(c => c.type === 'folder')
          .map(c => renderTreeItem(c, level + 1))}
      </div>
    );
  };

  /* ────────────────── render ────────────────── */
  return (
    <div className="cs-client-panel cs-client-panel--browser">

      {/* ── Step 1: search client ── */}
      <div className="cs-client-search">
        <div className="cs-client-search__input-wrap">
          <FiSearch size={15} className="cs-client-search__icon" />
          <input
            className="cs-client-search__input"
            placeholder="Enter client name"
            value={clientQuery}
            onChange={e => { setClientQuery(e.target.value); if (selectedClient) { setSelectedClient(null); setRootFolder(null); setTableData([]); } }}
            autoFocus
          />
          {clientLoading && <span className="cs-client-search__spinner" />}
          {selectedClient && (
            <button className="cs-client-search__clear" title="Clear"
              onClick={() => { setSelectedClient(null); setClientQuery(''); setClientList([]); setRootFolder(null); setTableData([]); setSelectedFile(null); onFileSelected(null); }}>
              <FiX size={14} />
            </button>
          )}
        </div>

        {clientList.length > 0 && !selectedClient && (
          <ul className="cs-client-dropdown">
            {clientList.map(c => (
              <li key={c.databaseId} className="cs-client-dropdown__item"
                onClick={() => { setSelectedClient(c); setClientQuery(c.name); setClientList([]); }}>
                <span className="cs-client-dropdown__name">{c.name}</span>
                {c.eMail && <span className="cs-client-dropdown__email">{c.eMail}</span>}
              </li>
            ))}
          </ul>
        )}
        {clientQuery.trim() && !clientLoading && clientList.length === 0 && !selectedClient && (
          <ul className="cs-client-dropdown">
            <li className="cs-client-dropdown__item cs-client-dropdown__item--empty">
              No clients found
            </li>
          </ul>
        )}
      </div>

      {/* ── Step 2: file browser ── */}
      {selectedClient && (
        <div className="cs-ams-wrap">

          {/* client bar */}
          <div className="cs-ams-client-bar">
            <FiUser size={13} className="cs-ams-client-bar__icon" />
            <span className="cs-ams-client-bar__name">{selectedClient.name}</span>
          </div>

          {/* loading skeleton */}
          {tableLoading && !rootFolder && (
            <div className="cs-ams-state">
              <span className="cs-ams-spinner" />
              <span>Loading files…</span>
            </div>
          )}

          {/* error */}
          {fetchError && <div className="cs-ams-error-msg">{fetchError}</div>}

          {/* browser layout */}
          {rootFolder && (
            <>
              {/* selected file card */}
              {selectedFile && (
                <div className="cs-ams-file-card">
                  <div className="cs-ams-file-card__left">
                    {/* <FiFile size={16} className="cs-ams-file-card__icon" /> */}
                    <div>
                      <p className="cs-ams-file-card__name" title={selectedFile.name}>{selectedFile.name}</p>
                      {selectedFile.size && selectedFile.size !== '-' && (
                        <p className="cs-ams-file-card__size">{fmtSize(selectedFile.size)}</p>
                      )}
                    </div>
                  </div>
                  <button className="cs-ams-file-card__clear"
                    onClick={() => { setSelectedFile(null); onFileSelected(null); }}>
                    <FiX size={13} />
                  </button>
                </div>
              )}

              {/* breadcrumb nav */}
              <div className="cs-ams-breadcrumb">
                <button className="cs-ams-nav-btn" disabled={!canGoBack}    onClick={handleBack}    title="Back">    <FiArrowLeft  size={13} /></button>
                <button className="cs-ams-nav-btn" disabled={!canGoForward} onClick={handleForward} title="Forward"> <FiArrowRight size={13} /></button>
                <div className="cs-ams-breadcrumb__path">
                  {pathSegments.map((seg, i) => (
                    <React.Fragment key={seg.id}>
                      {i > 0 && <span className="cs-ams-breadcrumb__sep">/</span>}
                      <span className="cs-ams-breadcrumb__seg" title={seg.name}>{seg.name}</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* tree + table */}
              <div className="cs-ams-layout">
                {/* left: folder tree */}
                <div className="cs-ams-tree">
                  {renderTreeItem(rootFolder)}
                </div>

                {/* right: file list */}
                <div className="cs-ams-table-wrap">
                  {tableLoading ? (
                    <div className="cs-ams-state">
                      <span className="cs-ams-spinner" />
                      <span>Loading…</span>
                    </div>
                  ) : tableData.length === 0 ? (
                    <div className="cs-ams-state cs-ams-state--empty">No files in this folder.</div>
                  ) : (
                    <table className="cs-ams-table">
                      <thead>
                        <tr>
                          <th className="cs-ams-th">Name</th>
                          <th className="cs-ams-th">Type</th>
                          <th className="cs-ams-th">Size</th>
                          <th className="cs-ams-th">Uploader</th>
                          <th className="cs-ams-th">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.map(item => {
                          const { Icon, color } = getIcon(item);
                          const isSelected = selectedFile?.id === item.id;
                          return (
                            <tr
                              key={item.id}
                              className={`cs-ams-tr${item.type === 'folder' ? ' cs-ams-tr--folder' : ' cs-ams-tr--file'}${isSelected ? ' cs-ams-tr--active' : ''}`}
                              onClick={() => {
                                if (item.type === 'folder') { toggleFolder(item); return; }
                                setSelectedFile(item);
                                onFileSelected({ file: item, insuredId: selectedClient!.databaseId, folderId: currentFolder?.id || '' });
                              }}
                            >
                              <td className="cs-ams-td cs-ams-td--name">
                                {/* <Icon size={14} style={{ color, flexShrink: 0 }} /> */}
                                <span title={item.name}>{item.name}</span>
                              </td>
                              <td className="cs-ams-td">{item.description || '—'}</td>
                              <td className="cs-ams-td">{fmtSize(item.size)}</td>
                              <td className="cs-ams-td">{item.uploader || '—'}</td>
                              <td className="cs-ams-td">{item.created || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CommissionScanClientFiles;

